import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  OnInit,
  Optional,
  Output,
  Self,
  ViewChild,
} from '@angular/core';
import { generateId } from '../../../_core/utils/generate-id';
import { MenuTriggerDirective } from '../menu/menu-trigger.directive';
import { Keys } from '../../../_core/utils/keys';

@Component({
  selector: 'opac-menu-item',
  templateUrl: './menu-item.component.html',
  styleUrls: ['./menu-item.component.scss']
})
export class MenuItemComponent implements OnInit {
  id: string = generateId();
  submenu: MenuTriggerDirective | null = null;
  tabindex = -1;

  get hasSubmenu(): boolean {
    return this.submenu !== null;
  }

  /**
   * Emits when option is selected
   */
  @Output() selected = new EventEmitter<void>();

  /**
   * Saves li element to handle native focus and click
   */
  @ViewChild('element') element!: ElementRef<HTMLElement>;

  /**
   * Sets has-submenu css class
   */
  @HostBinding('class.has-submenu')
  get classHasSubmenu() {
    return this.hasSubmenu;
  }

  constructor(@Self() @Optional() private readonly menuTrigger: MenuTriggerDirective | null) { }

  ngOnInit(): void {
    this.submenu = this.menuTrigger;

    // Set default positioning for submenu
    if (this.hasSubmenu) {
      this.submenu.isSubmenu = true;
      this.submenu.position = 'right-start';
    }
  }

  focus() {
    this.element.nativeElement.focus();
  }

  click() {
    this.element.nativeElement.click();
  }

  handleMouseover(event: MouseEvent) {
    this.focus();
    event.stopPropagation();

    if (this.hasSubmenu) {
      this.submenu.showPopover();
    }
  }

  handleKeydown(event: KeyboardEvent) {
    if (!this.hasSubmenu) {
      return;
    }

    // Handle opening/closing actions for submenu
    switch (event.key) {
      case Keys.Escape:
      case Keys.Tab:
        this.submenu.closePopover();
        break;
      case Keys.ArrowLeft:
        if (event.target !== this.element.nativeElement) {
          event.preventDefault();
          event.stopPropagation();
          this.focus();
          this.submenu.closePopover();
        }
        break;
      case Keys.ArrowRight:
      case Keys.Enter:
      case Keys.Space:
        this.handleOpenSubmenu();
        break;
    }
  }

  handleFocusout(event: FocusEvent) {
    if (event.relatedTarget && event.relatedTarget instanceof Element && this.element.nativeElement.contains(event.relatedTarget)) {
      return;
    }

    this.submenu?.closePopover();
  }

  private async handleOpenSubmenu() {
    await this.submenu.showPopover();
    const firstItem = this.submenu.menu.items.first;
    this.submenu.menu.setCurrentItem(firstItem);
    firstItem.focus();
  }
}
